/* Native Shopify filtering, with availability kept in history state instead of the address bar. */
(() => {
  'use strict';
  if (window.ShopstaCollection || window.Shopify?.designMode) return;
  let marker = document.querySelector('[data-shopsta-collection]');
  if (!marker) return;
  const sectionId = marker.dataset.sectionId;
  let section = marker.parentElement;
  const stockParam = 'filter.v.availability';
  const stateKey = 'shopstaCollection';
  const initial = new URL(location.href);
  let current;
  let request;
  let sequence = 0;
  let priceTimer;
  const cache = new Map();
  const cacheLifetime = 30000;
  const cacheLimit = 4;
  const cacheSizeLimit = 2000000;
  function cacheKey(url) {
    const key = new URL(url);
    key.searchParams.delete('section_id');
    key.searchParams.sort();
    return key.href;
  }
  function remember(url, html) {
    if (html.length > cacheSizeLimit) return;
    const key = cacheKey(url);
    cache.delete(key);
    cache.set(key, {html, expires: Date.now() + cacheLifetime});
    let size = [...cache.values()].reduce((sum, entry) => sum + entry.html.length, 0);
    while (cache.size > cacheLimit || size > cacheSizeLimit) {
      const oldest = cache.keys().next().value;
      size -= cache.get(oldest).html.length;
      cache.delete(oldest);
    }
  }
  function cached(url) {
    const key = cacheKey(url);
    const entry = cache.get(key);
    if (!entry) return null;
    if (Date.now() >= entry.expires) {
      cache.delete(key);
      return null;
    }
    cache.delete(key);
    cache.set(key, entry);
    return entry.html;
  }
  function restoreInitialSection() {
    if (marker.isConnected && marker.dataset.shopstaSectionType) {
      marker.setAttribute('data-section-type', marker.dataset.shopstaSectionType);
      delete marker.dataset.shopstaSectionType;
      marker.parentElement.dispatchEvent(new CustomEvent('shopify:section:load', {
        bubbles: true, detail: {sectionId}
      }));
    }
  }

  function clean(url) {
    const result = new URL(url);
    result.searchParams.delete(stockParam);
    result.searchParams.delete('section_id');
    return result;
  }
  function fromHistory() {
    const visible = new URL(location.href);
    const stored = history.state?.[stateKey];
    if (!visible.searchParams.has(stockParam) && stored) {
      try {
        const saved = new URL(stored, visible);
        if (clean(saved).href === clean(visible).href) return saved;
      } catch (_) { /* Ignore malformed state from older scripts. */ }
    }
    // Only the automatic default yields to an all-sold-out collection.
    // Explicit availability links and saved customer choices above remain authoritative.
    if (!visible.searchParams.has(stockParam) && marker.dataset.shopstaDefaultStock !== 'false') {
      visible.searchParams.set(stockParam, '1');
    }
    return visible;
  }
  function save(url, mode) {
    history[mode === 'push' ? 'pushState' : 'replaceState'](
      { ...history.state, [stateKey]: url.href }, '', clean(url).href
    );
  }
  function prepareLinks() {
    section.querySelectorAll('[data-filter-input], .filter-item-clear-all, .pagination--item[href]').forEach(link => {
      const href = link.dataset.shopstaUrl || link.getAttribute('href');
      if (!href) return;
      const url = new URL(href, current || initial);
      url.searchParams.delete('section_id');
      if (url.searchParams.has('grid_list') && !url.searchParams.get('grid_list')) {
        url.searchParams.set('grid_list', current?.searchParams.get('grid_list') || 'grid-view');
      }
      link.dataset.shopstaUrl = url.href;
      link.href = clean(url).href;
    });
  }
  function busy(value) {
    section.toggleAttribute('data-shopsta-loading', value);
    section.setAttribute('aria-busy', String(value));
    if (!value) document.documentElement.classList.remove('shopsta-collection-pending');
  }
  async function navigate(input, mode = 'push', focusSelector = '') {
    const url = new URL(input, current || location.href);
    if (url.origin !== location.origin || url.pathname !== initial.pathname) return;
    url.searchParams.delete('section_id');
    clearTimeout(priceTimer);
    request?.abort();
    const endpoint = new URL(url);
    endpoint.searchParams.set('section_id', sectionId);
    const preload = window.ShopstaCollectionPreload;
    const usePreload = preload?.sectionId === sectionId && preload.url === endpoint.href;
    // Adopt the early request exactly once, including its cancellation signal.
    if (preload) {
      window.ShopstaCollectionPreload = null;
      if (!usePreload) preload.controller.abort();
    }
    const controller = usePreload ? preload.controller : new AbortController();
    request = controller;
    const run = ++sequence;
    busy(true);
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      let html = cached(url);
      const fromCache = html !== null;
      if (html === null) {
        if (usePreload) html = await preload.promise;
        else {
          const response = await fetch(endpoint.href, { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Collection unavailable');
          html = await response.text();
        }
      }
      if (typeof html !== 'string') throw new Error('Collection unavailable');
      if (run !== sequence) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextMarker = parsed.querySelector('[data-shopsta-collection]');
      const next = nextMarker?.parentElement;
      if (!next || nextMarker.dataset.sectionId !== sectionId || !next.querySelector('[data-productgrid-outer]')) {
        throw new Error('Invalid collection section');
      }
      if (!fromCache) remember(url, html);
      // Close the theme's mobile filter/sort modal before unloading its section.
      document.querySelector('.modal--productgrid-sidebar [data-modal-close], .modal--productgrid-sort [data-modal-close]')?.click();
      if (section.querySelector('[data-section-type]')) {
        section.dispatchEvent(new CustomEvent('shopify:section:unload', { bubbles: true, detail: { sectionId } }));
      }
      // Keep the unchanged introduction/subcategory nodes, including read-more state.
      // Only the filter controls, products, counts and pagination need fresh DOM.
      const masthead = section.querySelector('.productgrid--masthead');
      const nextMasthead = next.querySelector('.productgrid--masthead');
      if (masthead && nextMasthead) nextMasthead.replaceWith(masthead);
      section.replaceWith(next);
      section = next;
      marker = nextMarker;
      current = url;
      save(url, mode);
      prepareLinks();
      section.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { sectionId } }));
      // Empire rewrites pagination's grid/list value when its section starts.
      prepareLinks();
      if (focusSelector) {
        const focus = section.querySelector(focusSelector) || section.querySelector('[data-productgrid-items], .productgrid--no-results');
        if (focus) {
          if (!focus.matches('a, button, input, select')) focus.setAttribute('tabindex', '-1');
          focus.focus({ preventScroll: true });
          if (focusSelector === '[data-productgrid-items]') focus.scrollIntoView({ block: 'start' });
        }
      }
    } catch (error) {
      if (run !== sequence) return;
      // Preserve the usable previous page on failure. Never redirect to a filtered URL.
      restoreInitialSection();
      if (current) save(current, 'replace');
      console.warn('Shopsta collection could not refresh', error);
    } finally {
      clearTimeout(timeout);
      if (preload) clearTimeout(preload.restoreTimer);
      if (run === sequence) busy(false);
    }
  }
  function isOurs(element) {
    return section.contains(element) || !!element.closest('.modal--productgrid-sidebar, .modal--productgrid-sort');
  }
  function stop(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
  function sort(element, event) {
    stop(event);
    const url = new URL(current || fromHistory());
    url.searchParams.set('sort_by', element.value);
    url.searchParams.delete('page');
    void navigate(url, 'push', '[data-productgrid-trigger-sort]');
  }
  document.addEventListener('click', event => {
    if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const target = event.target.closest('[data-filter-input], .filter-item-clear-all, .pagination--item[href], [data-collection-view], [data-productgrid-trigger-sort-button]');
    if (!target || !isOurs(target)) return;
    if (target.matches('[data-productgrid-trigger-sort-button]')) return sort(target, event);
    stop(event);
    if (target.hasAttribute('data-disabled')) return;
    if (target.hasAttribute('data-collection-view')) {
      const url = new URL(current || fromHistory());
      url.searchParams.set('grid_list', target.dataset.collectionView);
      void navigate(url, 'push', `[data-collection-view="${target.dataset.collectionView === 'list-view' ? 'list-view' : 'grid-view'}"]`);
      return;
    }
    const raw = target.dataset.shopstaUrl || target.dataset.url || target.getAttribute('href');
    const url = new URL(raw.startsWith('%') ? decodeURIComponent(raw) : raw, current || location.href);
    const paging = target.matches('.pagination--item');
    if (!paging) url.searchParams.delete('page');
    // Native filter links retain other filters; keep the selected sort/view on clear-all too.
    for (const name of ['sort_by', 'grid_list', 'view']) {
      if (!url.searchParams.has(name) && current?.searchParams.has(name)) url.searchParams.set(name, current.searchParams.get(name));
    }
    void navigate(url, 'push', paging ? '[data-productgrid-items]' : '[data-productgrid-trigger-filters]');
  }, true);
  function price(event) {
    const input = event.target;
    if (!input.matches('.collection-filters__filter-range-input') || !isOurs(input)) return false;
    event.stopImmediatePropagation();
    clearTimeout(priceTimer);
    priceTimer = setTimeout(() => {
      const url = new URL(current || fromHistory());
      input.closest('form').querySelectorAll('input[name]').forEach(field => {
        url.searchParams.delete(field.name);
        if (field.value.trim()) url.searchParams.set(field.name, field.value);
      });
      url.searchParams.delete('page');
      void navigate(url, 'push', '[data-productgrid-trigger-filters]');
    }, 700);
    return true;
  }
  document.addEventListener('input', price, true);
  document.addEventListener('keyup', event => {
    if (event.target.matches('.collection-filters__filter-range-input') && isOurs(event.target)) event.stopImmediatePropagation();
  }, true);
  document.addEventListener('change', event => {
    if (price(event)) return;
    if (event.target.matches('[data-productgrid-trigger-sort]') && isOurs(event.target)) sort(event.target, event);
  }, true);
  document.addEventListener('submit', event => {
    if (event.target.matches('.collection-filters__price-range-form') && isOurs(event.target)) stop(event);
  }, true);
  window.addEventListener('load', prepareLinks, { once: true });
  window.addEventListener('popstate', () => { void navigate(fromHistory(), 'replace'); });
  window.ShopstaCollection = { navigate };
  const desired = fromHistory();
  save(desired, 'replace');
  if (desired.href === initial.href) {
    current = desired;
    prepareLinks();
    busy(false);
  } else {
    void navigate(desired, 'replace');
  }
})();
